import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboard, MapPin, Home, Settings, Plus, Eye, Star, MessageSquare, Tag, Paintbrush, Menu, X, Quote } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import API from '../../lib/api';

export function AdminNav({ active }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const links = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/locations', label: 'Locations', icon: MapPin },
    { path: '/admin/listings', label: 'Listings', icon: Home },
    { path: '/admin/offers', label: 'Offers', icon: Tag },
    { path: '/admin/testimonials', label: 'Testimonials', icon: Quote },
    { path: '/admin/messages', label: 'Messages', icon: MessageSquare },
    { path: '/admin/editor', label: 'Editor', icon: Paintbrush },
    { path: '/admin/settings', label: 'Settings', icon: Settings },
  ];

  return (
    <>
      {/* Desktop nav */}
      <nav className="hidden md:flex items-center gap-1 mb-8 border-b border-white/[0.08] pb-4" data-testid="admin-nav">
        {links.map(l => (
          <Link key={l.path} to={l.path}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors whitespace-nowrap
              ${active === l.path ? 'bg-gold/15 text-gold' : 'text-white/40 hover:text-white hover:bg-white/[0.05]'}`}
            data-testid={`admin-nav-${l.label.toLowerCase()}`}
          >
            <l.icon className="w-4 h-4" /> {l.label}
          </Link>
        ))}
      </nav>

      {/* Mobile nav */}
      <div className="md:hidden mb-6" data-testid="admin-nav-mobile">
        <button onClick={() => setMobileOpen(!mobileOpen)}
          className="w-full flex items-center justify-between px-4 py-3 bg-[#1c1c1c] border border-white/[0.08] rounded-xl text-sm font-medium text-white"
          data-testid="admin-nav-mobile-toggle">
          <span className="flex items-center gap-2">
            {(() => { const cur = links.find(l => l.path === active); return cur ? <><cur.icon className="w-4 h-4 text-gold" /> {cur.label}</> : 'Menu'; })()}
          </span>
          {mobileOpen ? <X className="w-4 h-4 text-white/40" /> : <Menu className="w-4 h-4 text-white/40" />}
        </button>
        <AnimatePresence>
          {mobileOpen && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
              className="bg-[#1c1c1c] border border-white/[0.08] border-t-0 rounded-b-xl overflow-hidden -mt-1">
              {links.map(l => (
                <Link key={l.path} to={l.path} onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors border-b border-white/[0.04] last:border-0
                    ${active === l.path ? 'bg-gold/10 text-gold' : 'text-white/40 hover:text-white hover:bg-white/[0.03]'}`}
                  data-testid={`admin-nav-m-${l.label.toLowerCase()}`}
                >
                  <l.icon className="w-4 h-4" /> {l.label}
                </Link>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}

export default function AdminDashboard() {
  const { isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [stats, setStats] = useState(null);

  useEffect(() => { if (!authLoading && !isAdmin) { navigate('/login'); return; } if (isAdmin) API.get('/admin/dashboard').then(r => setStats(r.data)).catch(() => {}); }, [isAdmin, authLoading, navigate]);

  if (!stats) return <div className="min-h-screen pt-20 flex items-center justify-center"><div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>;

  const cards = [
    { label: 'Locations', value: stats.total_locations, icon: MapPin, color: 'text-blue-400', bg: 'bg-blue-500/10', link: '/admin/locations' },
    { label: 'Listings', value: stats.total_listings, icon: Home, color: 'text-emerald-400', bg: 'bg-emerald-500/10', link: '/admin/listings' },
    { label: 'Visible', value: stats.visible_listings, icon: Eye, color: 'text-amber-400', bg: 'bg-amber-500/10' },
    { label: 'Featured', value: stats.featured_listings, icon: Star, color: 'text-purple-400', bg: 'bg-purple-500/10' },
    { label: 'Messages', value: stats.total_messages, icon: MessageSquare, color: 'text-rose-400', bg: 'bg-rose-500/10' },
  ];

  return (
    <div className="min-h-screen pt-20 bg-[#101010]" data-testid="admin-dashboard">
      <div className="bg-[#161616] border-b border-white/[0.06] py-8 px-4 sm:px-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div><p className="text-gold text-xs uppercase tracking-[0.3em] font-semibold">Admin Panel</p><h1 className="font-heading text-2xl text-white font-bold mt-1">Dashboard</h1></div>
          <div className="flex gap-2">
            <Link to="/admin/locations/new"><motion.button whileHover={{ scale: 1.02 }} className="flex items-center gap-1.5 bg-gold text-white text-xs uppercase tracking-widest px-4 py-2.5 rounded-lg font-medium hover:bg-[#9A7B1F] transition-colors" data-testid="add-location-btn"><Plus className="w-3.5 h-3.5" /> Location</motion.button></Link>
            <Link to="/admin/listings/new"><motion.button whileHover={{ scale: 1.02 }} className="flex items-center gap-1.5 bg-white/[0.08] text-white text-xs uppercase tracking-widest px-4 py-2.5 rounded-lg font-medium hover:bg-white/[0.12] border border-white/[0.08] transition-colors" data-testid="add-listing-btn"><Plus className="w-3.5 h-3.5" /> Listing</motion.button></Link>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        <AdminNav active={loc.pathname} />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-10">
          {cards.map((c, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-5 cursor-pointer hover:border-white/[0.12] transition-all"
              onClick={() => c.link && navigate(c.link)}>
              <div className={`w-10 h-10 ${c.bg} rounded-lg flex items-center justify-center mb-3`}><c.icon className={`w-5 h-5 ${c.color}`} /></div>
              <p className="text-2xl font-bold text-white">{c.value}</p>
              <p className="text-xs text-white/35 uppercase tracking-widest mt-0.5">{c.label}</p>
            </motion.div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-6">
            <h3 className="font-heading text-base font-semibold text-white mb-4">Recent Locations</h3>
            {stats.recent_locations.length === 0 ? <p className="text-sm text-white/35">No locations yet.</p> : (
              <div className="space-y-3">{stats.recent_locations.map(loc => (
                <Link key={loc.id} to={`/admin/locations/${loc.id}/edit`} className="flex items-center justify-between p-3 rounded-lg hover:bg-white/[0.04] transition-colors group">
                  <div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-white/[0.06] overflow-hidden">{loc.thumbnail_image && <img src={loc.thumbnail_image.startsWith('http') ? loc.thumbnail_image : `${process.env.REACT_APP_BACKEND_URL}${loc.thumbnail_image}`} alt="" className="w-full h-full object-cover" />}</div>
                    <div><p className="text-sm font-medium text-white/70 group-hover:text-gold transition-colors">{loc.name}</p><p className="text-xs text-white/30">{loc.is_featured ? 'Featured' : ''}</p></div></div>
                </Link>
              ))}</div>
            )}
          </div>
          <div className="bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-6">
            <h3 className="font-heading text-base font-semibold text-white mb-4">Recent Listings</h3>
            {stats.recent_listings.length === 0 ? <p className="text-sm text-white/35">No listings yet.</p> : (
              <div className="space-y-3">{stats.recent_listings.map(l => (
                <Link key={l.id} to={`/admin/listings/${l.id}/edit`} className="flex items-center justify-between p-3 rounded-lg hover:bg-white/[0.04] transition-colors group">
                  <div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-white/[0.06] overflow-hidden">{l.cover_image && <img src={l.cover_image.startsWith('http') ? l.cover_image : `${process.env.REACT_APP_BACKEND_URL}${l.cover_image}`} alt="" className="w-full h-full object-cover" />}</div>
                    <div><p className="text-sm font-medium text-white/70 group-hover:text-gold transition-colors">{l.title}</p><p className="text-xs text-white/30">{l.location_name}</p></div></div>
                </Link>
              ))}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
